// Kindred Smart Shopping: wire protocol between the extension and the backend agent.

export const SMART_SHOPPING_NAMESPACE = "/smart-shopping";

/** Socket.IO event names. Client = extension, server = backend. */
export enum SmartShoppingSocketEvent {
  /** server → client once the handshake is accepted. */
  Ready = "ss:ready",
  /** client → server, with ack. Starts one agent turn. */
  Turn = "ss:turn",
  /** client → server. Stops the running turn. */
  Cancel = "ss:cancel",
  /** client → server. Answers an approval request. */
  Approval = "ss:approval",
  /** client → server, with ack. Thumbs up/down on a turn. */
  Feedback = "ss:feedback",
  /** client → server, with ack. Cheap promo + map lookup for the tab the user is on. */
  Peek = "ss:peek",
  /** server → client. One agent event. */
  Event = "ss:event",
  /** server → client, with ack. The agent asks the browser to do something. */
  Browser = "ss:browser",
}

export enum SmartShoppingAccessError {
  CodeRequired = "access_code_required",
  CodeInvalid = "access_code_invalid",
  Disabled = "smart_shopping_disabled",
}

export interface ISmartShoppingHandshake {
  accessCode?: string | null;
  /** Random per-install id, so anonymous quotas have something to key on. */
  installId: string;
  extensionVersion: string;
}

export interface ISmartShoppingReady {
  accessLabel: string | null;
  anonymous: boolean;
  remainingToday: number | null;
  features: {
    computerUse: boolean;
    judge: string | null;
    webSearch: boolean;
  };
  models: { agent: string; computer: string; judge: string | null };
}

export interface ISmartShoppingSettings {
  /** "auto" or a Kindred country code (UK, not GB). */
  market: string;
  /** Skip the approval card before acting on a page. */
  autoApprove: boolean;
  allowComputerUse: boolean;
}

export interface ISmartShoppingTabContext {
  tabId: number;
  url: string;
  title: string;
  domain: string | null;
}

export interface ISmartShoppingViewport {
  width: number;
  height: number;
}

export interface ISmartShoppingTurnRequest {
  conversationId: string;
  message: string;
  previousResponseId?: string | null;
  settings: ISmartShoppingSettings;
  locale: string;
  timeZone: string;
  tab: ISmartShoppingTabContext | null;
  viewport: ISmartShoppingViewport | null;
  /** Things the shopper asked us to remember (sizes, brands), kept in the extension. */
  preferences?: string[];
}

export type ISmartShoppingTurnAck =
  | { ok: true; turnId: string }
  | { ok: false; error: string; code?: string };

export interface ISmartShoppingApprovalResponse {
  turnId: string;
  approvalId: string;
  approved: boolean;
}

export interface ISmartShoppingFeedback {
  conversationId: string;
  turnId: string;
  rating: 1 | -1;
  comment?: string | null;
}

export interface ISmartShoppingPeekRequest {
  domain: string;
  market: string;
  locale: string;
}

export interface ISmartShoppingPeekResult {
  domain: string;
  market: string;
  merchantName: string | null;
  logoUrl: string | null;
  codeCount: number;
  offerCount: number;
  hasMerchantMap: boolean;
  canApplyCoupons: boolean;
  /** Cart and checkout URL patterns from the map, so the panel can tell when the shopper reaches checkout. */
  basketUrlPatterns?: string[];
}

// ── Steps (what the agent did) ─────────────────────────────

export enum SmartShoppingStepKind {
  Kindred = "kindred",
  Search = "search",
  Browser = "browser",
  Computer = "computer",
  Ui = "ui",
  Judge = "judge",
  Guardrail = "guardrail",
}

export enum SmartShoppingKindredApi {
  ProductMap = "product-map",
  PromoDiscovery = "promo-discovery",
  MerchantMap = "merchant-map",
}

export const SMART_SHOPPING_API_LABELS: Record<SmartShoppingKindredApi, { name: string; endpoint: string }> = {
  [SmartShoppingKindredApi.ProductMap]: { name: "Entity Mapping", endpoint: "POST /v1/OpenData/product-map" },
  [SmartShoppingKindredApi.PromoDiscovery]: { name: "Promo Discovery", endpoint: "GET /v1/opendata/promo-discovery" },
  [SmartShoppingKindredApi.MerchantMap]: { name: "Merchant Map", endpoint: "GET /v1/OpenData/merchant-map" },
};

export type SmartShoppingStepStatus = "running" | "ok" | "error" | "rejected";

export interface ISmartShoppingStep {
  id: string;
  kind: SmartShoppingStepKind;
  tool: string;
  api?: SmartShoppingKindredApi | null;
  /** Plain-language label for the activity list. */
  label: string;
  input?: unknown;
  startedAt: number;
  /** Set when the step runs inside the computer-use operator. */
  parentId?: string | null;
}

export interface ISmartShoppingStepMetrics {
  results?: number;
  screenshots?: number;
  mapActions?: number;
}

export interface ISmartShoppingStepResult {
  stepId: string;
  status: Exclude<SmartShoppingStepStatus, "running">;
  durationMs: number;
  summary: string;
  output?: unknown;
  error?: string | null;
  metrics?: ISmartShoppingStepMetrics;
}

// ── UI blocks the agent can show ───────────────────────────

export interface ISmartShoppingPriceBreakdown {
  price?: number | null;
  discount?: number | null;
  shipping?: number | null;
  total?: number | null;
  code?: string | null;
  note?: string | null;
}

export type SmartShoppingProductSource = "product-map" | "web" | "page";

/** What Kindred can do at this product's store: codes from Promo Discovery, applying them via Merchant Map. */
export interface ISmartShoppingStoreReach {
  codeCount: number;
  canApplyCodes: boolean;
  bestCode: string | null;
  /** Expected saving on this product's price from the best code, before it is tested. */
  expectedSaving: number | null;
  netPrice: number | null;
}

export interface ISmartShoppingProduct {
  ref: string | null;
  name: string;
  brand: string | null;
  merchantName: string | null;
  domain: string | null;
  imageUrl: string | null;
  url: string | null;
  price: number | null;
  originalPrice: number | null;
  currency: string | null;
  inStock: boolean | null;
  source: SmartShoppingProductSource;
  confidence: number | null;
  badges: string[];
  note: string | null;
  breakdown: ISmartShoppingPriceBreakdown | null;
  reach?: ISmartShoppingStoreReach | null;
}

export type SmartShoppingProductsLayout = "carousel" | "list" | "compare";

export interface ISmartShoppingProductsBlock {
  id: string;
  title: string;
  subtitle: string | null;
  layout: SmartShoppingProductsLayout;
  items: ISmartShoppingProduct[];
  total: { amount: number; currency: string; budget: number | null } | null;
}

export interface ISmartShoppingOffer {
  ref: string | null;
  domain: string;
  merchantName: string | null;
  logoUrl: string | null;
  code: string | null;
  title: string;
  promoType: string | null;
  discountLabel: string | null;
  minimumSpend: number | null;
  currency: string | null;
  endDate: string | null;
  coverage: string | null;
  link: string | null;
  /** Agent's note on who it applies to, e.g. "Existing customers". */
  eligibility: string | null;
}

export interface ISmartShoppingOffersBlock {
  id: string;
  title: string;
  domain: string;
  merchantName: string | null;
  items: ISmartShoppingOffer[];
}

export type SmartShoppingCodeOutcome = "saved" | "rejected" | "no-change" | "skipped" | "error";

export interface ISmartShoppingCodeResult {
  code: string;
  status: SmartShoppingCodeOutcome;
  total: number | null;
  saving: number | null;
  message: string | null;
  durationMs: number;
}

export type SmartShoppingDriver = "merchant-map" | "computer-use";

export interface ISmartShoppingSavingsBlock {
  id: string;
  domain: string;
  currency: string | null;
  before: number | null;
  after: number | null;
  bestCode: string | null;
  applied: boolean;
  results: ISmartShoppingCodeResult[];
  via: SmartShoppingDriver;
  durationMs: number;
  /** A code the shopper already had on, removed to measure it; kept means it beat ours and was put back. */
  preApplied?: { codes: string[]; saving: number; kept: boolean };
  /** Where the run's time went, for the debug view. */
  phases?: { prepareMs: number; testMs: number; applyMs: number };
}

export interface ISmartShoppingApprovalRequest {
  id: string;
  tool: string;
  title: string;
  detail: string;
  bullets: string[];
  domain: string | null;
  confirmLabel: string;
}

// ── Judge ──────────────────────────────────────────────────

export type SmartShoppingVerdict = "pass" | "warn" | "fail";

export interface ISmartShoppingJudgeCheck {
  id: string;
  label: string;
  verdict: SmartShoppingVerdict;
  note: string;
}

export interface ISmartShoppingJudgeResult {
  provider: string;
  model: string | null;
  verdict: SmartShoppingVerdict;
  score: number;
  summary: string;
  checks: ISmartShoppingJudgeCheck[];
  /** True when the answer was rewritten because of this verdict. */
  revised: boolean;
  durationMs: number;
}

// ── Usage ──────────────────────────────────────────────────

export interface ISmartShoppingUsage {
  model: string;
  requests: number;
  inputTokens: number;
  cachedInputTokens: number;
  outputTokens: number;
  costUsd: number | null;
  webSearches: number;
  kindredCalls: number;
  browserActions: number;
  mapActions: number;
  computerActions: number;
  screenshots: number;
}

// ── Events (server → client) ───────────────────────────────

export type SmartShoppingEvent =
  | { type: "turn.started"; turnId: string; conversationId: string; startedAt: number; model: string; market: string; currency: string }
  | { type: "status"; text: string }
  | { type: "reasoning.delta"; segmentId: string; delta: string }
  | { type: "text.delta"; segmentId: string; delta: string }
  /** Marks earlier text segments as replaced by a judge-driven revision. */
  | { type: "text.superseded"; segmentIds: string[]; reason: string }
  | { type: "step.started"; step: ISmartShoppingStep }
  | { type: "step.progress"; stepId: string; text: string }
  | { type: "step.finished"; result: ISmartShoppingStepResult }
  | { type: "ui.products"; block: ISmartShoppingProductsBlock }
  | { type: "ui.offers"; block: ISmartShoppingOffersBlock }
  | { type: "ui.savings"; block: ISmartShoppingSavingsBlock }
  | { type: "ui.suggestions"; suggestions: string[] }
  | { type: "approval.requested"; approval: ISmartShoppingApprovalRequest }
  | { type: "approval.resolved"; approvalId: string; approved: boolean }
  | { type: "judge.started" }
  | { type: "judge.result"; result: ISmartShoppingJudgeResult }
  | { type: "turn.completed"; responseId: string | null; durationMs: number; usage: ISmartShoppingUsage }
  | { type: "turn.failed"; message: string; retryable: boolean };

export interface ISmartShoppingEventEnvelope {
  turnId: string;
  seq: number;
  event: SmartShoppingEvent;
}

// ── Browser actions (server → client, answered by ack) ─────

/** The subset of a Kindred merchant map the page needs, with timings parsed to ms. */
export interface ISmartShoppingPageMap {
  domain: string;
  viewType: string;
  checkout: {
    toggleCouponBox: string | null;
    extraToggleCouponBox: string | null;
    couponBox: string | null;
    submitCode: string | null;
    failureAlert: string | null;
    successAlert: string | null;
    removeCode: string | null;
    confirmRemoveCode: string | null;
    preAppliedCode: string | null;
    nonRemovablePreAppliedCode: string | null;
    preAppliedSavings: string | null;
    finalPrice: string | null;
    discountAmount: string | null;
    deliveryAmount: string | null;
    taxAmount: string | null;
    dutiesAmount: string | null;
    shippingProtectionAmount: string | null;
    bagFeeAmount: string | null;
    miscFeeAmount: string | null;
    pageLoading: string | null;
    applyCodeTimeoutMs: number;
    removeCodeTimeoutMs: number;
    betweenActionMs: number;
    betweenCodesMs: number;
    /** The template stores these in seconds; converted here. */
    failureCheckMs: number;
    couponBoxCheckMs: number;
    hasSiteRefresh: boolean;
  };
  shipping: {
    name: string | null;
    address: string | null;
    email: string | null;
  };
  cart: {
    itemName: string | null;
    itemPrice: string | null;
    itemOriginalPrice: string | null;
    itemQuantity: string | null;
    itemProductId: string | null;
    itemProductUrl: string | null;
    itemCategory: string | null;
    basketAmount: string | null;
    emptyCart: string | null;
    loading: string | null;
  };
}

export type SmartShoppingComputerAction =
  | { type: "click"; x: number; y: number; button: string }
  | { type: "double_click"; x: number; y: number }
  | { type: "scroll"; x: number; y: number; scrollX: number; scrollY: number }
  | { type: "type"; text: string }
  | { type: "wait" }
  | { type: "move"; x: number; y: number }
  | { type: "keypress"; keys: string[] }
  | { type: "drag"; path: [number, number][] };

export type SmartShoppingBrowserAction =
  | { type: "page.snapshot"; maxChars: number }
  | { type: "page.open"; url: string }
  /** Opens a Kindred activation link in a background tab, waits for it to reach the store, then closes it. */
  | { type: "page.activate"; url: string }
  /** Both views of the map; the page uses the one matching its current layout and falls back to the other. */
  | { type: "map.readBasket"; maps: ISmartShoppingPageMap[] }
  /** Tried in order; the run stops at the first code that lowers the total. */
  | { type: "map.applyCodes"; maps: ISmartShoppingPageMap[]; codes: string[] }
  /** `selector` is the merchant map's add-to-bag selector; null means use the store's standard button. */
  | { type: "page.addToCart"; selector: string | null; quantity: number; sku: string | null }
  /** Fills the shopper's saved details; the values live in the extension and never reach the server. */
  | { type: "page.fillDetails"; maps: ISmartShoppingPageMap[] }
  /** Saves a shopper preference in the extension; it is sent back with later requests. */
  | { type: "prefs.remember"; note: string }
  | { type: "computer.begin"; goal: string }
  | { type: "computer.end" }
  | { type: "computer.screenshot" }
  | { type: "computer.action"; action: SmartShoppingComputerAction };

export interface ISmartShoppingBrowserRequest {
  turnId: string;
  stepId: string;
  action: SmartShoppingBrowserAction;
}

export interface ISmartShoppingBrowserResult {
  ok: boolean;
  error?: string | null;
  data?: unknown;
}

export interface ISmartShoppingPageSnapshot {
  url: string;
  title: string;
  domain: string;
  text: string;
  truncated: boolean;
  /** schema.org Product data found on the page, if any. */
  products: Array<Record<string, unknown>>;
  /** Visible price-like strings, top of page first. */
  prices: string[];
  viewport: ISmartShoppingViewport;
}

export interface ISmartShoppingBasketLine {
  name: string;
  price: number | null;
  originalPrice: number | null;
  quantity: number | null;
  /** From the cart template's product id, URL and category selectors, when mapped. */
  productId?: string | null;
  productUrl?: string | null;
  category?: string | null;
}

export interface ISmartShoppingBasket {
  url: string;
  currency: string | null;
  items: ISmartShoppingBasketLine[];
  basketTotal: number | null;
  finalTotal: number | null;
  discount: number | null;
  delivery: number | null;
  appliedCodes: string[];
  /** Store discounts the template marks as non-removable. */
  autoDiscounts: string[];
  /** Tax, duties and other fees the template maps, by name. */
  fees: Record<string, number>;
  couponBoxVisible: boolean;
  isEmpty: boolean;
  /** Map fields that resolved on this page, for the debug view. */
  resolved: string[];
  /** Which map view (Mobile/Desktop) matched the page. */
  viewType: string | null;
}

export interface ISmartShoppingAddToCartResult {
  /** null when the click happened but the bag count couldn't be read. */
  added: boolean | null;
  /** The exact variant was already in the bag, so nothing was added. */
  alreadyInBag: boolean;
  /** How many of that variant the bag holds now, when the store says. */
  bagQuantity: number | null;
  via: "merchant-map" | "page";
  buttonText: string | null;
  countBefore: number | null;
  countAfter: number | null;
  evidence: string;
  url: string;
}

export interface ISmartShoppingFillDetailsResult {
  profileSaved: boolean;
  filled: Array<{ field: string; via: "merchant-map" | "page" }>;
  /** Saved values with no matching field on the page. */
  missing: string[];
  viewType: string | null;
  url: string;
}

export interface ISmartShoppingApplyCodesResult {
  viewType: string | null;
  currency: string | null;
  before: number | null;
  after: number | null;
  bestCode: string | null;
  applied: boolean;
  results: ISmartShoppingCodeResult[];
  /** Fallback codes left untested because an earlier one already saved. */
  unneeded?: string[];
  /** A code the shopper already had on, removed to measure it; kept means it beat ours and was put back. */
  preApplied?: { codes: string[]; saving: number; kept: boolean };
  durationMs: number;
  /** Where the run's time went, for the debug view. */
  phases?: { prepareMs: number; testMs: number; applyMs: number };
}

// ── Admin (backoffice) ─────────────────────────────────────

export interface ISmartShoppingAccessCode {
  _id: string;
  code: string;
  label: string;
  active: boolean;
  dailyTurnLimit: number;
  expiresAt: string | null;
  createdBy: string | null;
  totalTurns: number;
  lastUsedAt: string | null;
  createdAt: string;
}

export interface ISmartShoppingAccessCodeInput {
  label: string;
  code?: string;
  dailyTurnLimit?: number;
  expiresAt?: string | null;
}

export interface ISmartShoppingAccessCodeUpdate {
  label?: string;
  active?: boolean;
  dailyTurnLimit?: number;
  expiresAt?: string | null;
}

export type SmartShoppingTurnStatus = "completed" | "failed" | "cancelled";

export interface ISmartShoppingStoredStep {
  id: string;
  kind: SmartShoppingStepKind;
  tool: string;
  api: SmartShoppingKindredApi | null;
  label: string;
  input: unknown;
  status: Exclude<SmartShoppingStepStatus, "running">;
  summary: string;
  durationMs: number;
  startedAt: number;
  error: string | null;
}

export interface ISmartShoppingStoredTurn {
  turnId: string;
  message: string;
  answer: string;
  market: string;
  domain: string | null;
  settings: ISmartShoppingSettings;
  status: SmartShoppingTurnStatus;
  error: string | null;
  startedAt: string;
  durationMs: number;
  steps: ISmartShoppingStoredStep[];
  blocks: Array<
    | { type: "products"; block: ISmartShoppingProductsBlock }
    | { type: "offers"; block: ISmartShoppingOffersBlock }
    | { type: "savings"; block: ISmartShoppingSavingsBlock }
  >;
  judge: ISmartShoppingJudgeResult | null;
  usage: ISmartShoppingUsage | null;
  feedback: { rating: 1 | -1; comment: string | null; at: string } | null;
  /** Kindred APIs that returned successfully in this turn. */
  apis?: SmartShoppingKindredApi[];
}

/**
 * Who did each job in a turn: a Kindred API, the extension's own generic page logic, AI (LLM page reading,
 * web search, vision), or nobody. Derived from the stored steps, so it also covers older sessions.
 */
export type SmartShoppingJobSource = "api" | "generic" | "ai" | "missing";

export interface ISmartShoppingJobEvaluation {
  job: string;
  source: SmartShoppingJobSource;
  api: SmartShoppingKindredApi | null;
  /** The step result this is based on. */
  evidence: string;
  /** What the API lacked for this job, when it fell back or failed. */
  gap: string | null;
}

export interface ISmartShoppingTurnEvaluation {
  apisUsed: SmartShoppingKindredApi[];
  jobs: ISmartShoppingJobEvaluation[];
  ai: {
    llmCalls: number;
    costUsd: number | null;
    webSearches: number;
    pageReads: number;
    visualRuns: number;
    screenshots: number;
    judged: boolean;
  };
  gaps: string[];
}

/** Turns in a session that used each Kindred API, and all three. */
export interface ISmartShoppingApiTurns {
  productMap: number;
  promoDiscovery: number;
  merchantMap: number;
  allThree: number;
}

export interface ISmartShoppingSessionSummary {
  conversationId: string;
  accessLabel: string | null;
  installId: string;
  firstMessage: string;
  turnCount: number;
  lastTurnAt: string;
  domains: string[];
  totalCostUsd: number;
  thumbsUp: number;
  thumbsDown: number;
  judgeFails: number;
  failedTurns: number;
  apiTurns: ISmartShoppingApiTurns;
}

export interface ISmartShoppingSession extends ISmartShoppingSessionSummary {
  extensionVersion: string | null;
  turns: Array<ISmartShoppingStoredTurn & { evaluation?: ISmartShoppingTurnEvaluation }>;
  createdAt: string;
}

export interface ISmartShoppingSessionsResponse {
  items: ISmartShoppingSessionSummary[];
  total: number;
}

export interface ISmartShoppingSessionsFilter {
  search?: string;
  accessLabel?: string;
  rating?: "up" | "down";
  judge?: "fail";
  limit?: number;
  skip?: number;
}

/** A product shoppers asked for that Entity Mapping couldn't find; the products team works this list. */
export type SmartShoppingProductMissKind = "search" | "product-page" | "basket-line";
export type SmartShoppingProductMissStatus = "open" | "reported" | "resolved";

export interface ISmartShoppingProductMiss {
  _id: string;
  /** Null for searches across all stores. */
  domain: string | null;
  market: string;
  kind: SmartShoppingProductMissKind;
  query: string;
  productUrl: string | null;
  sku: string | null;
  brand: string | null;
  bestConfidence: number | null;
  /** Turns that hit this miss; repeats inside one turn count once. */
  count: number;
  /** Turns where a later Entity Mapping search for the same thing found something (inferred by words/domain). */
  recoveredCount: number;
  recoveredInTurn: boolean;
  /** Basket matches that missed some lines but not all: which lines is unknown, so all names are listed. */
  unmatchedLines: { missed: number; of: number } | null;
  firstSeenAt: string;
  lastSeenAt: string;
  conversationIds: string[];
  exampleConversationId: string | null;
  exampleTurnId: string | null;
  status: SmartShoppingProductMissStatus;
  note: string | null;
  updatedBy: string | null;
  statusUpdatedAt: string | null;
  /** Turns since the status last changed, so a resolved miss that keeps happening stands out. */
  countSinceStatus: number;
}

export interface ISmartShoppingProductMissesFilter {
  domain?: string;
  market?: string;
  status?: SmartShoppingProductMissStatus;
  kind?: SmartShoppingProductMissKind;
  search?: string;
  limit?: number;
  skip?: number;
}

export interface ISmartShoppingProductMissesResponse {
  items: ISmartShoppingProductMiss[];
  total: number;
}

export interface ISmartShoppingProductMissUpdate {
  status: SmartShoppingProductMissStatus;
  note?: string | null;
}

export interface ISmartShoppingProductMissRebuildResult {
  dryRun: boolean;
  sessions: number;
  turns: number;
  misses: number;
  written: number;
}
